/**
 * What uses an account, for the Delete, Detach and Revoke confirms (spec "Delete, Detach and Revoke
 * confirmation"). Mount it inside the dialog's content: the counts are read only while it is open.
 * The count is advisory, so a failed read never blocks the action.
 */
import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';

import { removalImpactLines, type RemovalPath } from '../../lib/removalImpactCopy';
import { removalImpactLoading, useRemovalImpact } from './useRemovalImpact';

export function RemovalImpactSummary({ accountId, path }: { accountId: string; path: RemovalPath }): React.JSX.Element {
  const query = useRemovalImpact(accountId);
  // ACT2-1286: the Delete copy follows the account-repair flag, because the behaviour does.
  const repairEnabled = useWorkspaceOrNull()?.isAccountRepairEnabled ?? false;

  if (query.isPending || removalImpactLoading(query)) {
    return (
      <div role="status" aria-label="Counting what uses this account" className="flex flex-col gap-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }
  if (query.isError) {
    return <Callout variant="warning">Counts are unavailable. You can still continue.</Callout>;
  }
  const { lead, bullets } = removalImpactLines(query.data, path, { repairEnabled });
  return (
    <div className="flex flex-col gap-1.5 text-sm text-foreground" data-testid="removal-impact">
      <p>{lead}</p>
      {bullets.length > 0 && (
        <ul className="flex list-disc flex-col gap-1 pl-5">
          {bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
